import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { t } from 'i18next'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Badge, SectionLoadingState } from '../../../../components'
import {
  algorithmRunStatuses,
  CompensationMatrixAnalysisValidationStatus,
  compensationMatrixAnalysisValidationStatuses,
  NO_DATA_PLACEHOLDER,
} from '../../../../constants/constants'
import { useAlgorithmRunDetails } from '../../../../hooks'
import useWorkspaceDetails from '../../../../hooks/useWorkspaceDetails'
import { CompensationMatrixAnalysisMatchingRunResultExperiment } from '../../../../store/reducers/workspaceReducer'
import { SmartFunctionListPanelLayout } from '../../components/SmartFunctionListPanelLayout'
import {
  compensationMatrixAnalysisBadgeStatusColorKeys,
  compensationMatrixAnalysisValidationStatusToComponentMap,
} from '../CompensationMatrixAnalysisSmartFunctionDetailsPage'
import { CompensationMatrixAnalysisMatchingFile } from '../../../../constants/config'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 224px);
`

interface ExtendedCompensationMatrixAnalysisRunItem extends CompensationMatrixAnalysisMatchingRunResultExperiment {
  key: string
}

const CompensationMatrixAnalysisSmartFunctionDetailsPageMatchingPanel = () => {
  const { workspaceId, algorithmRunId } = useParams()

  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { loading, selectedAlgorithmRun, downloadFile } =
    useAlgorithmRunDetails({
      workspaceId,
      algorithmRunId,
      preventFetch: true,
    })

  useEffect(() => {
    downloadFile(CompensationMatrixAnalysisMatchingFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formattedData: ExtendedCompensationMatrixAnalysisRunItem[] = [
    {
      id: '1',
      key: '1',
      fileName: 'hard coded FileName',
      cytometer: 'Hard coded cytometer',
      compensation: 'compensation',
      validationStatus: compensationMatrixAnalysisValidationStatuses.SUCCESS,
      validationReason: 'Because it is hard coded',
      experimentName: `Hard coded name  - ${
        selectedWorkspace?.algorithmName
      } - ${selectedAlgorithmRun?.id || 0}`,
      confidenceScore: 1.1,
    },
  ]

  const columnsBasicTable: ColumnsType<CompensationMatrixAnalysisMatchingRunResultExperiment> =
    useMemo(
      () => [
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.validationStatus',
          ),
          dataIndex: 'validationStatus',
          key: 'validationStatus',
          width: '20%',
          render: (text, record) => (
            <>
              {record.validationStatus ? (
                <Badge
                  color={
                    compensationMatrixAnalysisBadgeStatusColorKeys[
                      record.validationStatus as CompensationMatrixAnalysisValidationStatus
                    ]
                  }
                  icon={
                    compensationMatrixAnalysisValidationStatusToComponentMap[
                      record.validationStatus as CompensationMatrixAnalysisValidationStatus
                    ]
                  }
                >
                  {t(
                    `smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.validationStatuses.${record.validationStatus}`,
                  )}
                </Badge>
              ) : (
                NO_DATA_PLACEHOLDER
              )}
            </>
          ),
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.validationReason',
          ),
          dataIndex: 'validationReason',
          key: 'validationReason',
          width: '80%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
      ],
      [],
    )

  const columnsDetailsTable: ColumnsType<CompensationMatrixAnalysisMatchingRunResultExperiment> =
    useMemo(
      () => [
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.fileName',
          ),
          dataIndex: 'src',
          key: 'src',
          width: '20%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.cytometer',
          ),
          dataIndex: 'cytometer',
          key: 'cytometer',
          width: '20%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.compensation',
          ),
          dataIndex: 'compensation',
          key: 'compensation',
          width: '20%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.validationStatus',
          ),
          dataIndex: 'validationStatus',
          key: 'validationStatus',
          width: '10%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.validationReason',
          ),
          dataIndex: 'validationReason',
          key: 'validationReason',
          width: '20%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.experimentName',
          ),
          dataIndex: 'experimentName',
          key: 'experimentName',
          width: '20%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
        {
          title: t(
            'smartFunctionDetailsPage.cellCartaCompensationMatrixAnalysis.tableTitles.confidenceScore',
          ),
          dataIndex: 'confidenceScore',
          key: 'confidenceScore',
          width: '10%',
          render: (text, record) => <>{text || NO_DATA_PLACEHOLDER}</>,
        },
      ],
      [],
    )

  const columns = useMemo(
    () =>
      !selectedAlgorithmRun?.runStatus ||
      selectedAlgorithmRun.runStatus === algorithmRunStatuses.FAILED
        ? columnsBasicTable
        : columnsDetailsTable,
    [columnsBasicTable, columnsDetailsTable, selectedAlgorithmRun],
  )

  return loading ? (
    <SectionLoadingStateContainer>
      <SectionLoadingState />
    </SectionLoadingStateContainer>
  ) : (
    <SmartFunctionListPanelLayout>
      <Table
        columns={columns}
        dataSource={formattedData}
        loading={loading}
        rowKey="key"
        pagination={false}
        scroll={{ y: 'calc(100vh - 190px)' }}
        rowClassName="page-list-table-row"
      />
    </SmartFunctionListPanelLayout>
  )
}

export default CompensationMatrixAnalysisSmartFunctionDetailsPageMatchingPanel
